/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 8 de noviembre 2025
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import { useEffect, useRef, useState } from 'react';

/**
 * Hook para prevenir que la pantalla se bloquee
 * Usa la Screen Wake Lock API cuando está disponible
 */
const useWakeLock = (shouldLock = false) => {
  const wakeLockRef = useRef(null);
  const [isSupported, setIsSupported] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    // Verificar si el navegador soporta Wake Lock
    const supported = 'wakeLock' in navigator;
    setIsSupported(supported);

    if (!supported) {
      console.warn('Wake Lock API no está soportada en este navegador');
    }
  }, []);

  useEffect(() => {
    const requestWakeLock = async () => {
      if (!isSupported || !shouldLock) {
        return;
      }

      try {
        // Solicitar el wake lock
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        setIsLocked(true);

        console.log('✅ Wake Lock activado - La pantalla no se bloqueará');

        // Listener para cuando se libera el wake lock
        wakeLockRef.current.addEventListener('release', () => {
          console.log('Wake Lock liberado');
          setIsLocked(false);
        });

      } catch (error) {
        console.error('Error al solicitar Wake Lock:', error);
        setIsLocked(false);
      }
    };

    const releaseWakeLock = async () => {
      if (wakeLockRef.current) {
        try {
          await wakeLockRef.current.release();
          wakeLockRef.current = null;
          setIsLocked(false);
          console.log('Wake Lock desactivado');
        } catch (error) {
          console.error('Error al liberar Wake Lock:', error);
        }
      }
    };

    if (shouldLock) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }

    // Cleanup al desmontar el componente
    return () => {
      releaseWakeLock();
    };
  }, [shouldLock, isSupported]);

  // Manejar visibilitychange para re-adquirir el wake lock si la página vuelve a ser visible
  useEffect(() => {
    if (!isSupported || !shouldLock) {
      return;
    }

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && shouldLock && !wakeLockRef.current) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          setIsLocked(true);
          console.log('Wake Lock re-adquirido después de visibilitychange');
        } catch (error) {
          console.error('Error al re-adquirir Wake Lock:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [shouldLock, isSupported]);

  return {
    isSupported,
    isLocked,
  };
};

export default useWakeLock;
